import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from "@angular/common/http";
import { firstValueFrom, Subject } from "rxjs";
import { OrganizationRow, OrganizationDetail, OrganizationDetailResponse, OrganizationsResponse } from "allai-admin-shared";
import { environment } from '../../../environments/environment.js';
import { SnackbarService } from '../snackbar.service.js';
import { SessionService } from "../session.service";
import { OrganizationsFilterService } from './organizations-filter.service.js';
import { PageLoaderService } from "../page-loader.service";
import { ActivatedRouteSnapshot } from '@angular/router';

/** Service that loads and caches all organizations in the organization list page */
@Injectable({
	providedIn: 'root'
})
export class OrganizationsService {
	/** Loaded organizations by id */
	private _organizationMap: Map<string, OrganizationRow>;
	private _organizationList: OrganizationRow[];
	private _total: number = 100;
	private _organizationDetail: OrganizationDetail;

	private newOrganizationsSubject = new Subject<void>();
	private newOrganizationDetailSubject = new Subject<void>();

	constructor(private http: HttpClient,
		private sessionService: SessionService,
		private organizationsFilterService: OrganizationsFilterService,
		private pageLoaderService: PageLoaderService,
		private snackbar: SnackbarService) {

		//clear things out if the user logs out
		this.sessionService.onLogout.subscribe(() => {
			this.clear();
		});

		//listen to filter changes
		this.organizationsFilterService.onNewFilter.subscribe(() => {
			this.pageLoaderService.showAndListen(this.resolveOrganizations());
		});
		//listen to detail changes
		this.organizationsFilterService.onNewDetailFilter.subscribe(() => {
			this.pageLoaderService.showAndListen(this.resolveOrganizationDetail());
		});
	}

	/** Resolver to get all organizations for the list if not already loaded */
	static resolveOrganizations(): Promise<OrganizationRow[]> {
		return inject(OrganizationsService).resolveOrganizations();
	}

	/** Resolver to get the organization that is clicked on */
	static resolveOrganizationDetail = async (route: ActivatedRouteSnapshot) => {
		return inject(OrganizationsService).resolveOrganizationDetail(route.params["id"]);
	}

	/** Resolver to get all organizations for the list if not already loaded */
	async resolveOrganizations(): Promise<OrganizationRow[]> {
		try {
			const params: HttpParams = this.organizationsFilterService.params;

			//load from server
			const response = await firstValueFrom(this.http.get<OrganizationsResponse>(`${environment.apiUrl}/organizations`, { params }));

			this._organizationMap = new Map<string, OrganizationRow>(response.organizations.map(value => [value.id, value]));
			this._organizationList = response.organizations;
			this._total = response.total;


			//dispatch that there was a change
			this.newOrganizationsSubject.next();
		} catch (e) {
			//clear filter on error, just in case
			this.organizationsFilterService.clear();

			console.error("Failed to load organizations from server", e);
			this.snackbar.error("Failed to download organizations");
		}

		return this._organizationList;
	}

	/** Resolver to get all organizations for the list if not already loaded */
	async resolveOrganizationDetail(organizationId?: string): Promise<OrganizationDetail> {

		try {
			const id = organizationId??this._organizationDetail.organization.id;
			const params: HttpParams = this.organizationsFilterService.detailParams;
			const organizationResponse = await firstValueFrom(this.http.get<OrganizationDetailResponse>(`${environment.apiUrl}/organizations/${id}`, { params }));

			this._organizationDetail = {
				organization: organizationResponse.organization,
				metrics: organizationResponse.metrics,
				users: organizationResponse.users,
				patientMetrics: organizationResponse.patientMetrics,
				dciMetrics: organizationResponse.dciMetrics
			}
			this.newOrganizationDetailSubject.next();
		} catch (e) {
		}
		return this._organizationDetail;
	}

	get onNewOrganizations() {
		return this.newOrganizationsSubject.asObservable();
	}

	get onNewOrganizationDetail() {
		return this.newOrganizationDetailSubject.asObservable();
	}


	/** Get an organization by id */
	getOrganization(id: string) {
		return this._organizationMap.get(id);
	}

	/** Clear out loaded organizations and force a reload */
	clear() {
		this._organizationMap = undefined;
		this._organizationList = undefined;
		this._total = 100;
	}

	/** Get the sorted organizations */
	get organizationList() {
		return this._organizationList;
	}

	/** Get the organization detail */
	get organizationDetail() {
		return this._organizationDetail;
	}

	get total(): number {
		return this._total;
	}

	set total(total: number) {
		this._total = total;
	}
}
